import './AdOps.scss';
import { useState } from 'react';
import cn from 'classnames';
import { AlertDialog } from '@Components/AlertDialog';
import { Page } from '@Components/Page';
import { useSetError } from '../../redux/modals';
import { AdOpsUtilFunctions, Platform, PlatformType } from './adops-constants';
import { ExtremeReachCampaignAdder } from './LinearTools/ExtremeReachCampaignAdder';
import { LinearCampaigns } from './LinearTools/LinearCampaigns';
import { LinearIngestions } from './LinearTools/LinearIngestions';
import { LogUpdates } from './LinearTools/LogUpdates';
import { SyndicationPostLogs } from './LinearTools/SyndicationPostLogs';
import { FlashtalkingBackfill } from './StreamingTools/FlashtalkingBackfill';
import { FlashtalkingCampaignAdder } from './StreamingTools/FlashtalkingCampaignAdder';
import { FlashtalkingCreativeLibraryAdder } from './StreamingTools/FlashtalkingCreativeLibraryAdder';
import { FlashtalkingSiteAdder } from './StreamingTools/FlashtalkingSiteAdder';
import { NexxenAdvertiserAdder } from './StreamingTools/NexxenAdvertiserAdder';
import { NexxenCostAdjuster } from './StreamingTools/NexxenCostAdjuster';
import { StreamingMappingAdder } from './StreamingTools/StreamingMappingAdder';
import { TransferByMethod } from './StreamingTools/TransferByMethod';
import { TransferSpendAndImpressions } from './StreamingTools/TransferSpendAndImpressions';
import { UnresolvedMappings } from './StreamingTools/UnresolvedMappings';
import { ValidatePlacement } from './StreamingTools/ValidatePlacement';

interface AdOpsProps {
  platform: PlatformType;
  validatePlacement?: boolean;
  mappings?: boolean;
}

interface ToolSectionProps {
  utils: AdOpsUtilFunctions;
}

const LinearTools = ({ utils }: ToolSectionProps) => (
  <div className='linear-tools-layout'>
    <div className='linear-tools-sidebar'>
      <LinearIngestions utils={utils} />
      <LogUpdates utils={utils} />
      <SyndicationPostLogs utils={utils} />
      <ExtremeReachCampaignAdder utils={utils} />
    </div>
    <LinearCampaigns utils={utils} />
  </div>
);

const StreamingTools = ({ utils }: ToolSectionProps) => (
  <>
    <div className='ad-ops-row'>
      <NexxenAdvertiserAdder utils={utils} />
      <FlashtalkingCampaignAdder utils={utils} />
    </div>
    <div className='ad-ops-row'>
      <FlashtalkingSiteAdder utils={utils} />
      <FlashtalkingCreativeLibraryAdder utils={utils} />
    </div>
    <div className='ad-ops-row'>
      <StreamingMappingAdder utils={utils} />
    </div>
    <div className='ad-ops-row'>
      <NexxenCostAdjuster utils={utils} />
      <TransferSpendAndImpressions utils={utils} />
    </div>
    <div className='ad-ops-row'>
      <TransferByMethod utils={utils} />
      <FlashtalkingBackfill utils={utils} />
    </div>
  </>
);

export const AdOps = ({ platform, validatePlacement = false, mappings = false }: AdOpsProps) => {
  const setError = useSetError();
  const [modalMessage, setModalMessage] = useState('');
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);

  const utils: AdOpsUtilFunctions = { setError, setModalMessage, setShowModalConfirmation };

  const isTableView = validatePlacement || mappings;

  return (
    <Page title={`${platform} AdOps Tools`} pageType={`${platform} AdOps Tools`}>
      <div
        className={cn('ad-ops-container', {
          'ad-ops-container-table': isTableView,
          // ValidatePlacement already provides its own canonical 16px page padding (matching
          // every other WidgetContainer+BPMTable page) -- the container's own 20px margin on top
          // of that double-spaces it relative to those pages.
          'ad-ops-container-validate-placement': validatePlacement
        })}
        data-testid='adops-page'
      >
        {platform === Platform.LINEAR && <LinearTools utils={utils} />}
        {platform === Platform.STREAMING && validatePlacement && <ValidatePlacement />}
        {platform === Platform.STREAMING && mappings && <UnresolvedMappings utils={utils} />}
        {platform === Platform.STREAMING && !validatePlacement && !mappings && (
          <StreamingTools utils={utils} />
        )}
      </div>
      <AlertDialog
        show={showModalConfirmation}
        onHide={() => setShowModalConfirmation(false)}
        title={modalMessage}
        body=''
        passthroughProps={{ 'data-testid': 'adops-confirmation' }}
      />
    </Page>
  );
};

// Thin routing entry points — `pageComponent` accepts a no-prop component, so each
// AdOps view is exposed as its own wrapper rather than passing props at the route level.
export const LinearAdOps = () => <AdOps platform={Platform.LINEAR} />;

export const StreamingAdOps = () => <AdOps platform={Platform.STREAMING} />;

export const StreamingValidatePlacement = () => (
  <AdOps platform={Platform.STREAMING} validatePlacement />
);

export const StreamingUnresolvedMappings = () => <AdOps platform={Platform.STREAMING} mappings />;
